import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Grid,
  TextInput,
  Modal,
  Group,
  Box,
  Select,
  Loader,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconAlertCircle, IconCopy, IconX, IconFileText, IconSettings, IconUsers, IconBrain, IconCalendar } from '@tabler/icons-react';
import { useAuthStore } from '@/stores/authStore';
import { useRoomStore } from '@/stores/roomStore';
import { roomsApi, authApi } from '@/services/api.service';
import { templatesApi } from '@/services/templates.api';
import DailyCallManager from '@/utils/dailyCallManager';
import type { DocumentTemplateListItem } from '@/types/template.types';

export const Dashboard = () => {
  const [joinCode, setJoinCode] = useState('');
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingJoin, setLoadingJoin] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [createdRoomCode, setCreatedRoomCode] = useState('');
  const [createdRoomTemplate, setCreatedRoomTemplate] = useState('');

  // Templates
  const [templates, setTemplates] = useState<DocumentTemplateListItem[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const canCreateRoom = useAuthStore((state) => state.canCreateRoom());
  const canJoinRoom = useAuthStore((state) => state.canJoinRoom());
  const isAdmin = useAuthStore((state) => state.isAdmin());
  const setRoom = useRoomStore((state) => state.setRoom);

  // Cleanup Daily.co instance when returning to dashboard
  useEffect(() => {
    DailyCallManager.destroyCallObject();
  }, []);

  // Load templates for room creation
  const loadTemplates = async () => {
    if (!canCreateRoom) return;

    setLoadingTemplates(true);
    try {
      const data = await templatesApi.getActiveForSelection();
      setTemplates(data);
      if (data.length > 0) {
        setSelectedTemplateId(String(data[0].id));
      }
    } catch (error: any) {
      console.error('Erro ao carregar templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Load templates when opening create modal
  const handleOpenCreateModal = async () => {
    setCreateModalOpened(true);
    if (templates.length === 0) {
      await loadTemplates();
    }
  };

  const handleCreateRoom = async () => {
    if (!selectedTemplateId) {
      notifications.show({
        title: 'Selecione um template',
        message: 'Escolha um template de documento para a sala',
        color: 'orange',
        icon: <IconAlertCircle size={18} />,
      });
      return;
    }

    setLoadingCreate(true);

    try {
      const room = await roomsApi.createRoom(parseInt(selectedTemplateId));
      setCreatedRoomCode(room.code);
      setCreatedRoomTemplate(room.templateTitle);
      setCreateModalOpened(false);
      setModalOpened(true);

      notifications.show({
        title: 'Sala criada com sucesso!',
        message: `Código: ${room.code}`,
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 4000,
      });
    } catch (error: any) {
      console.error('Erro ao criar sala:', error);

      let errorMessage = 'Não foi possível criar a sala.';

      if (error.response?.status === 401) {
        errorMessage = 'Sua sessão expirou. Faça login novamente.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.detail || 'Template inválido ou inativo.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro no servidor. Verifique a configuração da API Key do Daily.co.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Erro de conexão. Verifique sua internet.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      notifications.show({
        title: 'Erro ao criar sala',
        message: errorMessage,
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 6000,
      });
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleJoinRoom = async () => {
    if (!joinCode || joinCode.trim().length !== 6) {
      notifications.show({
        title: 'Código inválido',
        message: 'O código da sala deve ter exatamente 6 caracteres',
        color: 'orange',
        icon: <IconAlertCircle size={18} />,
        autoClose: 4000,
      });
      return;
    }

    setLoadingJoin(true);

    try {
      const room = await roomsApi.joinRoom(joinCode.toUpperCase().trim());
      setRoom(room);
      navigate(`/room/${room.code}`);
    } catch (error: any) {
      console.error('Erro ao entrar na sala:', error);

      let errorMessage = 'Não foi possível entrar na sala.';

      if (error.response?.status === 404) {
        errorMessage = 'Sala não encontrada ou expirada. Verifique o código.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Você não tem permissão para entrar nesta sala.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Erro de conexão. Verifique sua internet.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      notifications.show({
        title: 'Erro ao entrar na sala',
        message: errorMessage,
        color: 'red',
        icon: <IconX size={18} />,
        autoClose: 6000,
      });
    } finally {
      setLoadingJoin(false);
    }
  };

  const handleEnterCreatedRoom = async () => {
    try {
      const room = await roomsApi.getRoom(createdRoomCode);
      setRoom(room);
      setModalOpened(false);
      navigate(`/room/${room.code}`);
    } catch (error) {
      console.error('Erro ao entrar na sala criada:', error);
      notifications.show({
        title: 'Erro',
        message: 'Não foi possível entrar na sala',
        color: 'red',
      });
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(createdRoomCode);
    notifications.show({
      title: 'Código copiado!',
      message: 'Compartilhe com seus convidados',
      color: 'blue',
      icon: <IconCopy size={18} />,
      autoClose: 3000,
    });
  };

  const handleLogout = async () => {
    try {
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      logout();
      navigate('/');
    }
  };

  return (
    <>
      <Container size="md" py="xl">
        <Stack gap="xl">
          <div style={{ textAlign: 'center' }}>
            <Title order={1} mb="xs">
              Dashboard
            </Title>
            <Text c="dimmed">
              {isAdmin
                ? 'Gerencie usuários, templates e modelos LLM do sistema'
                : canCreateRoom
                  ? 'Crie uma nova sala ou entre em uma existente'
                  : 'Entre em uma sala existente'}
            </Text>
          </div>

          <Grid gutter="lg" justify="center">
            {/* Cards para Admin */}
            {isAdmin && (
              <>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <Paper
                    shadow="md"
                    p="xl"
                    radius="md"
                    style={{
                      height: '100%',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                    onClick={() => navigate('/admin')}
                  >
                    <Stack gap="md" align="center">
                      <Box
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <IconUsers size={32} />
                      </Box>
                      <Title order={3}>Gerenciar Usuários</Title>
                      <Text size="sm" c="dimmed" ta="center">
                        Crie, edite e gerencie os usuários do sistema
                      </Text>
                      <Button
                        size="lg"
                        fullWidth
                        onClick={() => navigate('/admin')}
                      >
                        Acessar
                      </Button>
                    </Stack>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <Paper
                    shadow="md"
                    p="xl"
                    radius="md"
                    style={{
                      height: '100%',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                    onClick={() => navigate('/templates')}
                  >
                    <Stack gap="md" align="center">
                      <Box
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <IconFileText size={32} />
                      </Box>
                      <Title order={3}>Gerenciar Templates</Title>
                      <Text size="sm" c="dimmed" ta="center">
                        Crie e gerencie os templates de documentos
                      </Text>
                      <Button
                        size="lg"
                        fullWidth
                        onClick={() => navigate('/templates')}
                      >
                        Acessar
                      </Button>
                    </Stack>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <Paper
                    shadow="md"
                    p="xl"
                    radius="md"
                    style={{
                      height: '100%',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                    onClick={() => navigate('/llm-models')}
                  >
                    <Stack gap="md" align="center">
                      <Box
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <IconBrain size={32} />
                      </Box>
                      <Title order={3}>Modelos LLM</Title>
                      <Text size="sm" c="dimmed" ta="center">
                        Configure o modelo de IA usado pelo sistema
                      </Text>
                      <Button
                        size="lg"
                        fullWidth
                        onClick={() => navigate('/llm-models')}
                      >
                        Acessar
                      </Button>
                    </Stack>
                  </Paper>
                </Grid.Col>
              </>
            )}

            {/* Card para Criar Sala (Analyst) */}
            {canCreateRoom && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Paper
                    shadow="md"
                    p="xl"
                    radius="md"
                    style={{
                      height: '100%',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <Stack gap="md" align="center">
                      <Box
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 32,
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      >
                        +
                      </Box>
                      <Title order={3}>Criar Nova Sala</Title>
                      <Text size="sm" c="dimmed" ta="center">
                        Inicie uma nova conferência e compartilhe o código com seus convidados
                      </Text>
                      <Button
                        size="lg"
                        fullWidth
                        onClick={handleOpenCreateModal}
                      >
                        Criar Sala
                      </Button>
                    </Stack>
                </Paper>
              </Grid.Col>
            )}

            {/* Card para Minhas Reuniões (Analyst) */}
            {canCreateRoom && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Paper
                    shadow="md"
                    p="xl"
                    radius="md"
                    style={{
                      height: '100%',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                    onClick={() => navigate('/meetings')}
                  >
                    <Stack gap="md" align="center">
                      <Box
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <IconCalendar size={32} />
                      </Box>
                      <Title order={3}>Minhas Reuniões</Title>
                      <Text size="sm" c="dimmed" ta="center">
                        Visualize o histórico de reuniões e edite campos preenchidos
                      </Text>
                      <Button
                        size="lg"
                        fullWidth
                        onClick={() => navigate('/meetings')}
                      >
                        Ver Reuniões
                      </Button>
                    </Stack>
                </Paper>
              </Grid.Col>
            )}

            {canJoinRoom && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Paper
                    shadow="md"
                    p="xl"
                    radius="md"
                    style={{
                      height: '100%',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <Stack gap="md" align="center">
                      <Box
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 32,
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      >
                        →
                      </Box>
                      <Title order={3}>Entrar em Sala</Title>
                      <Text size="sm" c="dimmed" ta="center">
                        Digite o código de 6 caracteres para entrar em uma sala existente
                      </Text>
                      <TextInput
                        placeholder="ABC123"
                        size="lg"
                        value={joinCode}
                        onChange={(e) =>
                          setJoinCode(e.currentTarget.value.toUpperCase())
                        }
                        maxLength={6}
                        style={{ width: '100%' }}
                      />
                      <Button
                        size="lg"
                        fullWidth
                        loading={loadingJoin}
                        onClick={handleJoinRoom}
                        disabled={joinCode.length !== 6}
                      >
                        Entrar
                      </Button>
                    </Stack>
                </Paper>
              </Grid.Col>
            )}
          </Grid>
        </Stack>
      </Container>

      {/* Modal de Criação - Seleção de Template */}
      <Modal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        title="Criar Nova Sala"
        centered
      >
        <Stack gap="md">
          <div>
            <Text size="sm" c="dimmed" mb="xs">
              Selecione o template de documento para esta sala:
            </Text>
            {loadingTemplates ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">Carregando templates...</Text>
              </Group>
            ) : templates.length === 0 ? (
              <Paper p="md" bg="orange.0" withBorder>
                <Group gap="xs">
                  <IconAlertCircle size={18} color="orange" />
                  <Text size="sm" c="orange.8">
                    Nenhum template ativo encontrado. Crie um template primeiro.
                  </Text>
                </Group>
              </Paper>
            ) : (
              <Select
                placeholder="Escolha um template"
                data={templates.map((t) => ({
                  value: String(t.id),
                  label: `${t.title} (v${t.version})`,
                }))}
                value={selectedTemplateId}
                onChange={setSelectedTemplateId}
                leftSection={<IconFileText size={16} />}
              />
            )}
          </div>

          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setCreateModalOpened(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateRoom}
              loading={loadingCreate}
              disabled={!selectedTemplateId || templates.length === 0}
            >
              Criar Sala
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal de Sucesso */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title="Sala Criada com Sucesso!"
        centered
      >
        <Stack gap="md">
          <div>
            <Text size="sm" c="dimmed" mb="xs">
              Código da sala:
            </Text>
            <Paper p="md" bg="gray.1">
              <Text size="xl" fw={700} ta="center">
                {createdRoomCode}
              </Text>
            </Paper>
          </div>

          {createdRoomTemplate && (
            <div>
              <Text size="sm" c="dimmed" mb="xs">
                Template de documento:
              </Text>
              <Group gap="xs">
                <IconFileText size={16} />
                <Text size="sm" fw={500}>{createdRoomTemplate}</Text>
              </Group>
            </div>
          )}

          <Text size="sm" c="dimmed">
            Compartilhe este código com as pessoas que você deseja convidar para a conferência.
          </Text>

          <Group grow>
            <Button variant="light" onClick={handleCopyCode}>
              Copiar Código
            </Button>
            <Button onClick={handleEnterCreatedRoom}>Entrar na Sala</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
